/**
 * /accounting/close-readiness — Continuous Close Dashboard.
 *
 * Wave D-1: one screen that tells the controller "are the books ready to close?"
 * 0-100 score derived live from every other accounting surface — no separate
 * data, no drift. Each signal links straight to the page that fixes it.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, AlertCircle } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useAP } from "@/data/accounting/ap/APContext";
import { useBankFeed } from "@/data/accounting/bankFeed/BankFeedContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import {
  getCloseReadiness, type ReadinessSignal, type SignalStatus,
} from "@/data/accounting/closeReadiness";

const STATUS_RING: Record<SignalStatus, string> = {
  green: "ring-emerald-500/40 bg-emerald-500/5",
  yellow: "ring-amber-500/40 bg-amber-500/5",
  red: "ring-rose-500/40 bg-rose-500/5",
};
const STATUS_DOT: Record<SignalStatus, string> = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-rose-500",
};
const STATUS_LABEL: Record<SignalStatus, string> = {
  green: "Ready",
  yellow: "Needs attention",
  red: "Blocked",
};
const STATUS_ICON: Record<SignalStatus, typeof CheckCircle2> = {
  green: CheckCircle2,
  yellow: AlertTriangle,
  red: AlertCircle,
};

export default function CloseReadinessPage() {
  useSeedGeneralLedger();
  const gl = useGeneralLedger();
  const ap = useAP();
  const bf = useBankFeed();

  const report = useMemo(() => getCloseReadiness({
    journalEntries: gl.journalEntries,
    accounts: gl.accounts,
    trialBalance: gl.getTrialBalance(),
    bills: ap.bills,
    bankTxns: bf.transactions,
  }), [gl, ap.bills, bf.transactions]);

  const HeaderIcon = STATUS_ICON[report.status];

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/gl" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to General Ledger
          </Link>
          <h1 data-tour-id="close-readiness-score" className="text-2xl font-semibold tracking-tight">Close Readiness</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Continuous close — composite score across every health signal in the ledger. Period: <span className="font-mono">{report.period}</span>
          </p>
        </div>
      </div>

      {/* Composite score */}
      <CockpitSection>
        <div className={`rounded-lg border-2 border-transparent ring-2 ${STATUS_RING[report.status]} p-6 flex items-center gap-6`}>
          <div className="flex-shrink-0">
            <div className="text-5xl font-semibold tabular-nums">{report.score}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">out of 100</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <HeaderIcon className={`h-5 w-5 ${report.status === "green" ? "text-emerald-600" : report.status === "yellow" ? "text-amber-600" : "text-rose-600"}`} />
              <span className="font-medium">{STATUS_LABEL[report.status]}</span>
              <span className="text-sm text-muted-foreground">
                · {report.signals.filter(s => s.status === "green").length} of {report.signals.length} signals clean
              </span>
            </div>
            {/* Composite bar */}
            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
              {report.signals.map(s => (
                <div
                  key={s.key}
                  className={STATUS_DOT[s.status]}
                  style={{ width: `${s.weight}%`, opacity: s.status === "green" ? 1 : 0.55 }}
                  title={`${s.label}: ${Math.round(s.score)}/${s.weight}`}
                />
              ))}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Each segment is weighted by its impact on close-readiness.
            </div>
          </div>
        </div>
      </CockpitSection>

      {/* Signal cards */}
      <CockpitSection>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Signals</div>
        <div className="grid gap-3 md:grid-cols-2">
          {report.signals.map(s => <SignalCard key={s.key} signal={s} />)}
        </div>
      </CockpitSection>

      <div className="text-[11px] text-muted-foreground text-center mt-4">
        Updated live from ledger state. No separate data — recomputes on every JE, payment, or bank match.
      </div>
    </CockpitPage>
  );
}

function SignalCard({ signal }: { signal: ReadinessSignal }) {
  const pct = signal.weight === 0 ? 0 : Math.round((signal.score / signal.weight) * 100);
  return (
    <Link
      to={signal.route}
      className={`block rounded-md border border-border bg-card hover:bg-accent/40 transition-colors p-4 ring-1 ${STATUS_RING[signal.status]}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${STATUS_DOT[signal.status]}`} />
          <span className="font-medium text-sm">{signal.label}</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span className="tabular-nums">{Math.round(signal.score)}/{signal.weight}</span>
          <ArrowRight className="h-3 w-3" />
        </div>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden mb-3">
        <div className={STATUS_DOT[signal.status]} style={{ width: `${pct}%`, height: "100%" }} />
      </div>
      <div className="text-sm">{signal.detail}</div>
      {signal.status !== "green" && (
        <div className="text-xs text-muted-foreground mt-1.5">{signal.remedy}</div>
      )}
    </Link>
  );
}
